import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import Cookies from 'js-cookie';
import { UUIDs } from '../ble/ble';
import DeviceConnection from '../ble/DeviceConnection';
import MapTable from '../ble/MapTable';
import useMapCollection from './useMapCollection';
import { useProtobufDeviceContext } from '../ble/protobufService/ProtobufDeviceContext';
import { googleMapEdit, googleMapKML } from '../settings';
import './CompassMap.css';

const CompassMap = () => {
  const [file, setFile] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoUpload, setAutoUpload] = useState(Cookies.get('autoUpload') === 'true');
  const [accumulatePoints, setAccumulatePoints] = useState(Cookies.get('accumulatePoints') === 'true');
  const [applyDate, setApplyDate] = useState(Cookies.get('applyDate') === 'true');
  const [applySorting, setApplySorting] = useState(Cookies.get('applySorting') === 'true');
  const [date, setDate] = useState(() => {
    const storedDate = Cookies.get('date');
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const formattedTomorrow = tomorrow.toISOString().slice(0, 10);
    if (!storedDate || new Date(storedDate) < new Date()) {
      Cookies.set('date', formattedTomorrow, { expires: 365 });
      return formattedTomorrow;
    }
    return storedDate;
  });
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [mapSource, setMapSource] = useState('');
  const [radius, setRadius] = useState(Cookies.get('radius') || 0);

  // Use the shared protobuf device context
  const {
    deviceConnectionState,
    isDeviceConnected,
    protobufCharacteristics,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
    connectionState,
    isInitialized,
    handleConnect,
    handleDisconnect,
    handleConnectionStateChange,
    sentPoints,
    setSentPoints,
    protobufProtocolService // <-- add this line
  } = useProtobufDeviceContext();

  // Find the map collection characteristic
  const mapCollectionCharacteristic = protobufCharacteristics.find(char => 
    char.isCollection && char.collectionData?.recordProtobufType === 'MapPoint'
  );

  // Use the map collection hook
  const mapCollection = useMapCollection(
    mapCollectionCharacteristic?.collectionData?.statusUuid,
    mapCollectionCharacteristic?.collectionData?.recordUuid,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    protobufProtocolService // <-- pass the service explicitly
  );

  // Sync local points with collection points when they change
  useEffect(() => {
    if (mapCollection.localPoints.length > 0) {
      // Allow real-time updates during device downloads (when mapSource is 'compass')
      // Only prevent updates during other loading operations
      if (mapSource === 'compass' && (!loading || mapCollection.isSyncing)) {
        handlePoints(mapCollection.localPoints, false, false);
      }
    }
  }, [mapCollection.localPoints, loading, mapSource, mapCollection.isSyncing]);

  // Load collection status after device is connected and collection is initialized
  useEffect(() => {
    let cancelled = false;
    async function loadStatusIfReady() {
      if (isDeviceConnected && mapCollection.initialized && mapCollection.loadCollectionStatus) {
        //setLoading(true);
        setError("");
        try {
          await mapCollection.loadCollectionStatus();
        } catch (err) {
          if (!cancelled) setError("Failed to load collection status: " + (err?.message || err));
        } finally {
          //if (!cancelled) setLoading(false);
        }
      }
    }
    loadStatusIfReady();
    return () => { cancelled = true; };
  }, [isDeviceConnected, mapCollection.initialized]);

  const handleRadiusChange = (event) => {
    const value = event.target.value;
    setRadius(value);
    Cookies.set('radius', value, { expires: 365 });
  };

  const handleAccumulatePointsChange = (event) => {
    setAccumulatePoints(event.target.checked);
    Cookies.set('accumulatePoints', event.target.checked, { expires: 365 });
  };

  const handleApplyDateChange = (event) => {
    const checked = event.target.checked;
    setApplyDate(checked);
    Cookies.set('applyDate', checked, { expires: 365 });

    if (checked) {
      const updatedPoints = reprocessPointsWithDate(points, date);
      setPoints(updatedPoints);
    }
  };

  const handleApplySortingChange = (event) => {
    const checked = event.target.checked;
    setApplySorting(checked);
    Cookies.set('applySorting', checked, { expires: 365 });

    if (checked) {
      const sortedPoints = sortPoints(points);
      setPoints(sortedPoints);
    }
  };

  const handleDateChange = (event) => {
    const value = event.target.value;
    setDate(value);
    Cookies.set('date', value, { expires: 365 });

    if (applyDate) {
      const updatedPoints = reprocessPointsWithDate(points, value);
      setPoints(updatedPoints);
    }
  };

  const reprocessPointsWithDate = (pointsToProcess, selectedDate) => {
    const newDate = selectedDate.slice(2).replace(/-/g, '');
    const updatedPoints = pointsToProcess.map((point) => {
      let startStr = point.start.toString().slice(-4).padStart(4, '0');
      let endStr = point.end.toString().slice(-4).padStart(4, '0');

      if (endStr === '0000') {
        endStr = newDate + '2359';
      } else {
        endStr = newDate + endStr;
      }

      startStr = newDate + startStr;

      return {
        ...point,
        start: parseInt(startStr, 10),
        end: parseInt(endStr, 10),
      };
    });

    return updatedPoints;
  };

  const sortPoints = (pointsToSort) => {
    const sortedPoints = [...pointsToSort];

    sortedPoints.sort((a, b) => {
      if (a.name.toLowerCase() === 'home' || a.id === '0') {
        a.interactivity = 1;
        return -1;
      }
      if (b.name.toLowerCase() === 'home' || b.id === '0'){
        b.interactivity = 1;
        return 1;
      } 
      if (a.end !== b.end) return a.end - b.end;
      if (a.start !== b.start) return a.start - b.start;
      return a.name.localeCompare(b.name);
    });

    return sortedPoints;
  };

  const handlePoints = (newPoints, forceAdd = false, accumulate = accumulatePoints) => {
    let updatedPoints = newPoints;

    if (applyDate) {
      updatedPoints = reprocessPointsWithDate(newPoints, date);
    }

    if (!accumulate && !forceAdd) {
      setPoints(updatedPoints);
    } else {
      setPoints((prevPoints) => {
        const finalPoints = [...prevPoints];

        updatedPoints.forEach((newPoint) => {
          const match = finalPoints.find((point) =>
            point.id === newPoint.id &&
            point.start === newPoint.start &&
            point.end === newPoint.end
          );
          if (!match || forceAdd) {
            finalPoints.push(newPoint);
          }
        });

        return finalPoints;
      });
    }

    if (applySorting) {
      setPoints((prevPoints) => sortPoints(prevPoints));
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      setError('');
      setProgress({ current: 0, total: 0 });
      const response = await axios.post(`https://convert-kml-to-map-points-fzjaqefbta-uc.a.run.app?radius=${radius}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Response data:', response.data);
      handlePoints(response.data.data, false, accumulatePoints);
      setMapSource('file');
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAndUpload = async () => {
    try {
      setLoading(true);
      setProgress({ current: 0, total: 0 });
      const response = await axios.get(googleMapKML, { responseType: 'blob' });
      const file = new Blob([response.data], { type: 'application/vnd.google-earth.kml+xml' });
      await handleUpload(file);
      setMapSource('default map');
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download and upload file. Please try again.');
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setError('');
      handleUpload(selectedFile);
    }
  }, [radius, accumulatePoints]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/vnd.google-earth.kmz': ['.kmz'],
      'application/gpx+xml': ['.gpx']
    },
  });

  // New protobuf-based map operations
  const handleFetchMapPoints = async () => {
    if (!mapCollectionCharacteristic) {
      setError('Map collection characteristic not found. Please ensure the device supports map collections.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setPoints([]);
      setProgress({ current: 0, total: 0 });
      setSentPoints([]);
      setMapSource('compass');
      
      await mapCollection.downloadPointsFromCollection((current, total) => {
        setProgress({ current, total });
      });
      
      // The useEffect will handle setting the points and mapSource
      // Update progress when download is complete
      if (mapCollection.localPoints.length > 0) {
        setProgress({ current: mapCollection.localPoints.length, total: mapCollection.localPoints.length });
      }
    } catch (error) {
      console.error('Error requesting map data:', error);
      setError('Failed to request map data from the device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMap = async (uploadPoints) => {
    if (!mapCollectionCharacteristic) {
      setError('Map collection characteristic not found. Please ensure the device supports map collections.');
      return;
    }

    try {
      setLoading(true);
      setSentPoints([]);
      setError('');
      setProgress({ current: 0, total: uploadPoints.length });
      
      console.log(`📤 Starting upload of ${uploadPoints.length} points to device...`);
      
      await mapCollection.uploadPointsToCollection(uploadPoints, (current, total) => {
        console.log(`📤 Upload progress: ${current}/${total}`);
        setProgress({ current, total });
        // Add the current point index to sentPoints (0-based index)
        setSentPoints(prev => [...new Set([...prev, current - 1])]);
      }, true);
      
      // Update progress when upload is complete
      setProgress({ current: uploadPoints.length, total: uploadPoints.length });
      // Mark all points as sent when upload is complete
      setSentPoints(uploadPoints.map((_, index) => index));
      
      console.log('✅ Map data uploaded successfully');
    } catch (error) {
      console.error('❌ Error uploading map data:', error);
      setError('Failed to upload map data to the device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoUploadChange = (event) => {
    setAutoUpload(event.target.checked);
    Cookies.set('autoUpload', event.target.checked, { expires: 365 });
  };

  const handleDeviceConnect = async () => {
    setSentPoints([]);
    
    // Connect to the protobuf service after device connection
    try {
      await handleConnect();
    } catch (error) {
      console.error('Error connecting to protobuf service:', error);
    }
    
    if (autoUpload && points.length > 0 && mapSource !== 'compass') {
      handleUploadMap(points);
    }
  };

  const handleDeviceDisconnect = () => {
    setSentPoints([]);
    handleDisconnect();
  };

  const handleCleanMap = () => {
    setPoints([]);
    setFile(null);
    setError('');
    setMapSource('');
    setProgress({ current: 0, total: 0 });
  };

  useEffect(() => {
    if (autoUpload && points.length === 0) {
      handleDownloadAndUpload();
    }
  }, [autoUpload]);

  if (!isInitialized) {
    return <div>Initializing Protobuf Protocol Service...</div>;
  }

  return (
    <div className="compass-map">
      <DeviceConnection
        onConnect={handleDeviceConnect}
        onDisconnect={handleDeviceDisconnect}
        onConnectionStateChange={handleConnectionStateChange}
        ServiceUUID={UUIDs.protobufService}
        externalConnectionState={deviceConnectionState}
      />
      
      <div className="compass-map-layout">
        {/* Left Column - Settings */}
        <div className="left-column">
          <div className="map-settings section">
            <h3>Settings</h3>
            <p>
              <label>
                <input type="checkbox" checked={autoUpload} onChange={handleAutoUploadChange} />
                Upload map automatically
              </label>
            </p>
            <p>
              <label>
                <input type="checkbox" checked={accumulatePoints} onChange={handleAccumulatePointsChange} />
                Accumulate points
              </label>
            </p>
            <p>
              <label>
                <input type="checkbox" checked={applyDate} onChange={handleApplyDateChange} />
                Apply date
              </label>
            </p>
            <p>
              <label>
                <input type="checkbox" checked={applySorting} onChange={handleApplySortingChange} />
                Apply sorting
              </label>
            </p>
            <p>
              <label>
                Date:
                <input
                  type="date"
                  value={date}
                  onChange={handleDateChange}
                />
              </label>
            </p>
            <p>
              <label>
                Radius:
                <input
                  type="number"
                  value={radius}
                  onChange={handleRadiusChange}
                  min="0"
                  step="1"
                />
              </label>
            </p>
            <p>
              <button onClick={handleCleanMap}>
                Clean map
              </button>
            </p>
          </div>

          {isDeviceConnected && (
            <div className="device-buttons section">
              <button onClick={handleFetchMapPoints} className="active-button" disabled={loading || mapCollection.isSyncing}>
                {loading || mapCollection.isSyncing
                  ? 'Reading...'
                  : `Read map from compass${mapCollection.collectionStatus ? ` (${mapCollection.collectionStatus.currentRecords})` : ''}`}
              </button>
              <button
                onClick={() => handleUploadMap(points)}
                className="active-button"
                disabled={loading || mapCollection.isSyncing || points.length === 0}
              >
                {loading || mapCollection.isSyncing
                  ? 'Uploading...'
                  : `Upload map to compass${points.length > 0 ? ` (${points.length})` : ''}`}
              </button>
              {mapSource === 'compass' && (
                <span>Source: 📱 Device</span>
              )}
              {mapSource === 'file' && (
                <span>Source: 📁 KML File</span>
              )}
              {mapSource === 'default map' && (
                <span>Source: 🗺️ Default Map</span>
              )}

              {mapCollection.isConnected && (
                <span className="connection-status">
                  {mapCollection.isSyncing ? '🔄 Syncing...' : '✅ Connected'}
                </span>
                )}
            </div>
          )}
        </div>

        {/* Right Column - File Upload and Default Map */}
        <div className="right-column">
          <div className="file-upload-section section">
            <h3>File Upload</h3>
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              {file ? (
                <p>{file.name}</p>
              ) : (
                <p>Drag 'n' drop a KMZ/KML/GPX file here, or click to select one</p>
              )}
            </div>
          </div>

          <div className="default-map-section section">
            <h3>Default Map</h3>
            <p>
              <button onClick={handleDownloadAndUpload} className="active-button" disabled={loading}>
                {loading ? 'Downloading...' : 'Download default map'}
              </button>
            </p>
            <p>
              <a href={googleMapEdit} target="_blank" rel="noopener noreferrer">
                Edit default map
              </a>
            </p>
          </div>
        </div>
      </div>
      
      {error && <p className="error-message">{error}</p>}

      {/* Map Table for all points */}
      <div className="map-table-container">
        <MapTable 
          points={points}
          progress={progress}
          sentPoints={sentPoints}
        />
      </div>
    </div>
  );
};

export default CompassMap; 